import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  destroyDevice,
  ensureDevice,
  getDevice,
  setRelanceurTwilio,
  setTokenRefreshCallback,
} from '@/lib/twilioDeviceSingleton'
import { invalidateVoiceToken, useVoiceToken } from '@/api/voice'
import { useAuthStore } from '@/stores/useAuthStore'
import { changerEtatTelephone } from '@/stores/useEtatTelephoneStore'

/** Twilio voice tokens live 1 h: past this age, fetch a new one before starting again. */
const AGE_MAX_JETON_MS = 50 * 60 * 1000

/**
 * Hook lifecycle React pour le Device Twilio Voice SDK.
 *
 * À monter UNE seule fois (typiquement dans VoiceProvider au-dessus du dashboard).
 *
 * - Récupère le token via TanStack Query (cache + retry)
 * - Crée le Device singleton via ensureDevice(), et le recrée s'il manque (relancerTwilio)
 * - Register le device après création
 * - Branche le callback tokenWillExpire pour refetch + updateToken
 * - Cleanup à la vraie déconnexion seulement : destroyDevice
 */
export function useTwilioDevice(): { ready: boolean } {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated())
  const queryClient = useQueryClient()
  const tokenQuery = useVoiceToken(isAuthenticated)
  const [ready, setReady] = useState(false)
  const [relance, setRelance] = useState(0)
  const ageJetonRef = useRef(0)
  ageJetonRef.current = tokenQuery.dataUpdatedAt

  // relancerTwilio(): a fresh token if ours is old (the new token starts the
  // Device), otherwise build the missing Device or register it again.
  useEffect(() => {
    setRelanceurTwilio(() => {
      if (Date.now() - ageJetonRef.current > AGE_MAX_JETON_MS) {
        void invalidateVoiceToken(queryClient)
        return
      }
      const device = getDevice()
      if (!device) setRelance((n) => n + 1)
      else if (device.state === 'unregistered') {
        device.register().catch((err: unknown) => console.error('[useTwilioDevice] register failed:', err))
      }
    })
    return () => setRelanceurTwilio(null)
  }, [queryClient])

  // Builds the Device when there is a token and no Device: first start, or
  // again after a failed start.
  useEffect(() => {
    const token = tokenQuery.data?.token
    if (!token || getDevice()) return

    let cancelled = false
    changerEtatTelephone('twilio', 'connexion')

    void (async () => {
      try {
        const device = await ensureDevice(token)
        if (cancelled) return

        // Listeners
        device.removeAllListeners('registered')
        device.removeAllListeners('unregistered')

        device.on('registered', () => {
          setReady(true)
          changerEtatTelephone('twilio', 'pret')
        })
        device.on('unregistered', () => {
          setReady(false)
          changerEtatTelephone('twilio', 'reconnexion')
        })

        // Refresh callback : quand tokenWillExpire, invalide la query
        setTokenRefreshCallback(async () => {
          await invalidateVoiceToken(queryClient)
        })

        // Register le device
        if (device.state === 'unregistered') {
          await device.register()
        } else if (device.state === 'registered') {
          setReady(true)
          changerEtatTelephone('twilio', 'pret')
        }
      } catch (err) {
        console.error('[useTwilioDevice] init failed:', err)
        changerEtatTelephone('twilio', 'coupe')
      }
    })()

    return () => {
      cancelled = true
    }
  }, [tokenQuery.data?.token, relance, queryClient])

  // Quand le token change (tokenWillExpire, ou un jeton neuf demandé par
  // relancerTwilio) : updateToken sur le Device existant, puis register s'il
  // était tombé.
  useEffect(() => {
    const device = getDevice()
    const token = tokenQuery.data?.token
    if (!device || !token) return
    try {
      device.updateToken(token)
    } catch (err) {
      console.error('[useTwilioDevice] updateToken failed:', err)
    }
    if (device.state === 'unregistered') {
      device.register().catch((err: unknown) => console.error('[useTwilioDevice] register failed:', err))
    }
  }, [tokenQuery.data?.token])

  // Logout or leaving the dashboard. Only a real logout (tokens cleared) turns
  // the phone off: a session renewal after a nap used to, and nothing turned
  // it back on until the voice token changed (24 Oct 2026).
  useEffect(() => {
    return () => {
      if (useAuthStore.getState().accessToken) return
      destroyDevice()
      setTokenRefreshCallback(null)
    }
  }, [])

  return { ready }
}
